import { ResponseCode, logAnalyticsEvent } from '@vestico/utils';

import { AuthApi, AuthInfo } from '../model/api';
import { AbstractApiClient } from './apiClient';
import { fetchRequest, fetchTokenRequestURL, getJWTTokenExpiry } from './helpers';

interface AuthInfoResponse {
  apiKey: string;
  token: string;
  expiry: number; // ms left until expiration
}

export class AuthApiClient extends AbstractApiClient implements AuthApi {
  public async fetchAuthInfo(isRefetch = false): Promise<AuthInfo | null> {
    const fetchURL = fetchTokenRequestURL(this.clientId, isRefetch, this.isSDK, this.sdkAPIKey);

    if (!fetchURL) {
      return null;
    }

    try {
      const tokenResponse = await fetchRequest({
        url: fetchURL,
        additionalHeaders: isRefetch ? { 'cache-control': 'no-cache, must-revalidate' } : undefined,
      });
      if (!tokenResponse || tokenResponse.status !== ResponseCode.success) {
        console.error('coud not fetch token');

        logAnalyticsEvent({
          category: 'exception',
          event: 'failed to fetch token',
          name: `${tokenResponse.status}`,
        });
        return null;
      }
      const authInfoResponse = (await tokenResponse.json()) as AuthInfoResponse | null;
      if (authInfoResponse) {
        const { apiKey, token, expiry } = authInfoResponse;

        // Subtract 10s to account for travel to client
        const jitterOffset = 10000;
        const jwtExpiry = getJWTTokenExpiry(token) * 1000 - jitterOffset;
        const expiryDate = Date.now() + expiry - jitterOffset;

        // Only fire if there's more than 10 minutes difference between expiry dates
        if (Math.abs(jwtExpiry - expiryDate) > 600000) {
          console.warn('JWT expiry mismatch');
          logAnalyticsEvent({
            category: 'exception',
            event: 'expiry-mismatch',
            name: `${tokenResponse.status}`,
            dimensions: {
              provider: this.clientId,
              auth_info_expiry: expiryDate,
              jwt_expiry: jwtExpiry,
              now_date: Date.now(),
            },
          });
        }

        const newAuthInfo: AuthInfo = {
          apiKey,
          token,
          expiryDate,
        };
        return newAuthInfo;
      }
      return null;
    } catch (error) {
      console.error(error);
      logAnalyticsEvent({
        category: 'exception',
        event: 'auth req',
        name: error.message,
      });
      return null;
    }
  }
}
